import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chessboard } from "react-chessboard";
import type { ChessboardOptions } from "react-chessboard";
import { PositionTracker } from "@onchainreps/chess";
import { StockfishEngine, type EngineResult } from "@onchainreps/engine";
import type { LocalStore } from "@onchainreps/storage";

interface BotGameProps {
  store: LocalStore | null;
}

type DifficultyId = "beginner" | "casual" | "club" | "strong";

interface Difficulty {
  id: DifficultyId;
  label: string;
  detail: string;
  depth: number;
  margin: number;
  skill: number;
}

const DIFFICULTIES: Difficulty[] = [
  { id: "beginner", label: "Beginner", detail: "Loose play, misses tactics", depth: 5, margin: 90, skill: 2 },
  { id: "casual", label: "Casual", detail: "Club players welcome", depth: 9, margin: 45, skill: 8 },
  { id: "club", label: "Club", detail: "Sharp and purposeful", depth: 13, margin: 20, skill: 13 },
  { id: "strong", label: "Strong", detail: "Full strength, no mercy", depth: 16, margin: 0, skill: 20 },
];

function pickEngineMove(result: EngineResult, margin: number): string | null {
  if (!result.bestMove) return null;
  const lines = result.lines.filter((line) => line.score);
  if (lines.length === 0 || margin <= 0) return result.bestMove;

  const best = lines[0]?.score;
  if (!best) return result.bestMove;

  const candidates = lines.filter((line) => {
    const score = line.score;
    if (!score) return false;
    if (score.type !== best.type) return false;
    return Math.abs(score.value - best.value) <= margin;
  });

  if (candidates.length === 0) return result.bestMove;
  const chosen = candidates[Math.floor(Math.random() * candidates.length)];
  return chosen?.pv[0] ?? result.bestMove;
}

export function BotGame({ store }: BotGameProps) {
  const [difficulty, setDifficulty] = useState<DifficultyId>("casual");
  const [color, setColor] = useState<"white" | "black">("white");
  const [phase, setPhase] = useState<"setup" | "playing" | "over">("setup");
  const [fen, setFen] = useState(PositionTracker ? new PositionTracker().fen : "");
  const [history, setHistory] = useState<string[]>([]);
  const [thinking, setThinking] = useState(false);
  const [evalText, setEvalText] = useState<string>("—");
  const [result, setResult] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const trackerRef = useRef<PositionTracker>(new PositionTracker());
  const engineRef = useRef<StockfishEngine | null>(null);
  const difficultyRef = useRef<Difficulty>(DIFFICULTIES[1]!);
  const colorRef = useRef<"white" | "black">("white");

  useEffect(() => {
    difficultyRef.current = DIFFICULTIES.find((entry) => entry.id === difficulty) ?? DIFFICULTIES[1]!;
  }, [difficulty]);

  useEffect(() => {
    colorRef.current = color;
  }, [color]);

  useEffect(() => {
    return () => {
      engineRef.current?.dispose();
      engineRef.current = null;
    };
  }, []);

  const think = useCallback(async (tracker: PositionTracker) => {
    const settings = difficultyRef.current;
    setThinking(true);
    try {
      if (!engineRef.current) {
        engineRef.current = new StockfishEngine({ threads: 1, hashMb: 32, multiPv: 3 });
        await engineRef.current.init();
        engineRef.current.setOption("Skill Level", settings.skill);
      }
      const result = await engineRef.current.analyse(tracker.fen, { depth: settings.depth });
      const move = pickEngineMove(result, settings.margin);
      if (!move) return;
      const applied = tracker.move(move);
      if (!applied.legal) return;
      setFen(tracker.fen);
      setHistory([...tracker.moves]);
      const line = result.line;
      if (line?.score) {
        const value = line.score.type === "mate" ? `mate ${line.score.value}` : `${(line.score.value / 100).toFixed(2)}`;
        setEvalText(`${value} (${line.depth})`);
      }
      if (tracker.isOver) {
        setPhase("over");
        setResult(tracker.status);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The engine stopped responding.");
    } finally {
      setThinking(false);
    }
  }, []);

  const start = useCallback(() => {
    const tracker = new PositionTracker();
    trackerRef.current = tracker;
    engineRef.current?.dispose();
    engineRef.current = null;
    setFen(tracker.fen);
    setHistory([]);
    setResult("");
    setError(null);
    setEvalText("—");
    setPhase("playing");
    if (colorRef.current === "black") void think(tracker);
  }, [think]);

  const options: ChessboardOptions = useMemo(() => {
    const tracker = trackerRef.current;
    const userTurn = tracker.sideToMove === colorRef.current;
    return {
      position: fen,
      boardOrientation: colorRef.current,
      allowDragging: phase === "playing" && userTurn && !thinking,
      showNotation: true,
      animationDurationInMs: 160,
      darkSquareStyle: { backgroundColor: "#3a5a77" },
      lightSquareStyle: { backgroundColor: "#dbe6f0" },
      boardStyle: { borderRadius: "6px", boxShadow: "none" },
      onPieceDrop: ({ sourceSquare, targetSquare }) => {
        if (!sourceSquare || !targetSquare) return false;
        if (phase !== "playing" || thinking) return false;
        const tracker = trackerRef.current;
        if (tracker.sideToMove !== colorRef.current) return false;
        const applied = tracker.move(`${sourceSquare}${targetSquare}`);
        if (!applied.legal) return false;
        setFen(tracker.fen);
        setHistory([...tracker.moves]);
        if (tracker.isOver) {
          setPhase("over");
          setResult(tracker.status);
          void persistBotGame(store, tracker, colorRef.current);
          return true;
        }
        void think(tracker);
        return true;
      },
    };
  }, [color, error, fen, history, phase, store, thinking, think]);

  const settings = DIFFICULTIES.find((entry) => entry.id === difficulty) ?? DIFFICULTIES[1]!;

  return (
    <div className="trainer">
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Play the engine</h1>
          {phase === "playing" ? (
            <span className="chip chip--live">
              <span className="chip__dot" />
              {thinking ? "Engine thinking" : "Your move"}
            </span>
          ) : null}
        </div>
        <div className="panel__body">
          {phase === "setup" ? (
            <>
              <p className="notice">
                Stockfish runs in this tab, so the opponent is local, instant and free. Pick a level and a
                colour.
              </p>

              <div className="field">
                <span className="field__label">Level</span>
                <div className="chiprow">
                  {DIFFICULTIES.map((entry) => (
                    <button
                      key={entry.id}
                      className={`chip chip--toggle${difficulty === entry.id ? " chip--on" : ""}`}
                      onClick={() => setDifficulty(entry.id)}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
                <span className="field__hint">{settings.detail}</span>
              </div>

              <div className="field">
                <span className="field__label">You play</span>
                <div className="segmented">
                  {(
                    [
                      ["white", "White"],
                      ["black", "Black"],
                    ] as Array<["white" | "black", string]>
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      role="tab"
                      aria-selected={color === id}
                      onClick={() => setColor(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {error ? <p className="notice notice--error">{error}</p> : null}

              <div className="btnrow">
                <button className="btn btn--primary" onClick={start}>
                  Start game
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="botboard">
                <Chessboard options={options} />
              </div>

              <div className="drill__meta">
                <div className="drill__prompt">
                  <span className="drill__kicker">
                    {phase === "over" ? "Game over" : thinking ? "Engine thinking" : "Your move"}
                  </span>
                  <strong>
                    {phase === "over"
                      ? result === "checkmate"
                        ? "Checkmate"
                        : result === "stalemate"
                          ? "Stalemate"
                          : "Draw"
                      : `You play ${color}`}
                  </strong>
                  <span className="drill__sub">Engine evaluation {evalText}</span>
                </div>
                <button
                  className="btn btn--inline"
                  onClick={() => {
                    setPhase("setup");
                    setFen(new PositionTracker().fen);
                    setHistory([]);
                  }}
                >
                  New game
                </button>
              </div>

              {history.length > 0 ? (
                <div className="movelist movelist--flat">
                  <div className="movelist__row">
                    {history.map((san, index) => (
                      <span className="move" key={`${san}-${index}`}>
                        {index % 2 === 0 ? `${index / 2 + 1}.` : ""} {san}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {error ? <p className="notice notice--error">{error}</p> : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

async function persistBotGame(
  store: LocalStore | null,
  tracker: PositionTracker,
  color: "white" | "black",
): Promise<void> {
  if (!store) return;
  const pgn = tracker.toPgn({
    Event: "OnchainReps bot game",
    White: color === "white" ? "You" : "Stockfish",
    Black: color === "black" ? "You" : "Stockfish",
    Result: tracker.status === "checkmate" ? (tracker.sideToMove === "white" ? "0-1" : "1-0") : "*",
  });
  const id = `bot_${Date.now().toString(36)}`;
  await store.saveGames([
    {
      id,
      source: "manual",
      pgn,
      white: color === "white" ? "You" : "Stockfish",
      black: color === "black" ? "You" : "Stockfish",
      result: pgn.match(/\[Result "([^"]+)"\]/)?.[1] ?? "*",
      opening: null,
      eco: null,
      playedAt: new Date().toISOString(),
      importedAt: new Date().toISOString(),
      accuracyWhite: null,
      accuracyBlack: null,
      blundersWhite: 0,
      blundersBlack: 0,
      playerColor: color,
    },
  ]);
}
